// FilesPanelV3 — collapsible "raw uploads" surface in V3.
//
// Each file uploaded via the upload endpoint (CSV / JSON / XLSX / …)
// is stored once at gs://datahub-events/<src>/raw_uploads/<date>/<id>.<ext>
// and parsed events land in the events view. This panel surfaces the
// raw blobs so the operator can: download the original, re-trigger the
// parse (reprocess), or audit what was sent.
//
// Hidden by default. Renders only when there's at least one upload.

import React, { useEffect, useState } from 'react';
import {
  listRawUploads,
  rawUploadDownloadUrl,
  reprocessRawUpload,
  type RawUpload,
} from '../../api';
import { useToast } from '../Toast';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(1)} MiB`;
}

export function FilesPanelV3({ sourceId, reloadKey, onReprocessed }: {
  sourceId: string;
  reloadKey: number;
  onReprocessed: () => void;
}): React.ReactElement | null {
  const toast = useToast();
  const [uploads, setUploads] = useState<RawUpload[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    listRawUploads(sourceId).then(setUploads).catch(() => setUploads([]));
  }, [sourceId, reloadKey]);

  if (uploads === null || uploads.length === 0) return null;

  async function handleReprocess(u: RawUpload): Promise<void> {
    setBusyId(u.upload_id);
    try {
      await reprocessRawUpload(sourceId, u.upload_id, true);
      toast.show(`Reprocessing ${u.original_filename}…`, { kind: 'success' });
      onReprocessed();
    } catch (e) {
      toast.show(`Reprocess failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ marginTop: 14, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--ink)', fontFamily: 'var(--ui)', fontSize: 12.5,
        }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{open ? '▾' : '▸'}</span>
        <strong>Uploaded files ({uploads.length})</strong>
        <span style={{ flex: 1 }} />
        {!open && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {uploads[0]!.original_filename} · {fmtBytes(uploads[0]!.size_bytes)}{uploads.length > 1 ? ` + ${uploads.length - 1} more` : ''}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {uploads.map(u => {
            const busy = busyId === u.upload_id;
            return (
              <div
                key={u.upload_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  padding: '6px 10px',
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink)', fontWeight: 500 }}>{u.original_filename}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>{fmtBytes(u.size_bytes)} · {new Date(u.created_at).toLocaleString()}</span>
                <span style={{ flex: 1 }} />
                <a
                  href={rawUploadDownloadUrl(sourceId, u.upload_id)}
                  className="btn sm ghost"
                  style={{ padding: '2px 10px', fontSize: 11, textDecoration: 'none' }}
                >download</a>
                <button
                  type="button"
                  onClick={() => { void handleReprocess(u); }}
                  disabled={busy}
                  className="btn sm ghost"
                  style={{ padding: '2px 10px', fontSize: 11 }}
                  title="Re-parse the file as if uploaded fresh — useful after a parser fix"
                >{busy ? 'reprocessing…' : 'reprocess'}</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
